import React, {
    forwardRef,
    useRef,
    useImperativeHandle,
    useCallback,
} from 'react';
import { get, noop } from 'lodash';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import ErrorMessage from 'components/Common/ErrorMessage';
import { useNodeContext } from 'contexts/NodeProvider';
import './Input.scss';

const TextField = forwardRef(
    (
        {
            label,
            name,
            value,
            onKeyDown,
            onKeyUp,
            disabled,
            errorMessage,
            onChange,
            maxLength,
            normalize,
            required,
            ...rest
        },
        ref
    ) => {
        return (
            <Form.Group className="text-field">
                {label && (
                    <Form.Label
                        className="text-field-label"
                        id={`label-inputquestion-${label}`}
                        htmlFor={name}
                    >
                        {label}
                        <span className="text-danger">
                            {required ? ' *' : ' '}
                        </span>
                    </Form.Label>
                )}
                <Form.Control
                    ref={ref}
                    data-testid={name}
                    type="input"
                    name={name}
                    className="text-field-input"
                    onChange={(e) => {
                        const val = normalize(e.target.value, value);
                        onChange(val);
                    }}
                    id={name}
                    value={value}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    maxLength={maxLength}
                    aria-labelledby={`label-inputquestion-${label}`}
                    disabled={disabled}
                    {...rest}
                />
                <ErrorMessage message={errorMessage} />
            </Form.Group>
        );
    }
);

const TextFieldWithController = forwardRef(
    (
        {
            control,
            name,
            defaultValue = '',
            label,
            type,
            onKeyUp,
            normalize,
            disabled,
            required,
            value,
            onChange = noop,
            registerRef = true,
            ...rest
        },
        ref
    ) => {
        const _ref = useRef();
        const { nodesRef } = useNodeContext();

        useImperativeHandle(ref, () => _ref.current);

        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({
                    field: {
                        onChange: handleChange,
                        value: formValue,
                        ref: rhfRef,
                    },
                    fieldState: { error },
                }) => {
                    const onRefChange = useCallback((node) => {
                        if (registerRef) {
                            _ref.current = node;
                            rhfRef = _ref;
                            if (nodesRef) {
                                nodesRef.current[name] = node;
                            }
                        }
                    }, []);
                    return (
                        <div className="text-field-container">
                            <TextField
                                ref={onRefChange}
                                name={name}
                                value={value ?? formValue ?? ''}
                                type={type}
                                onChange={(val) => {
                                    onChange(val);
                                    handleChange(val);
                                }}
                                normalize={normalize}
                                label={label}
                                option={{ label: name }}
                                onKeyUp={onKeyUp}
                                disabled={disabled}
                                errorMessage={get(error, 'message')}
                                required={required}
                                {...rest}
                            />
                        </div>
                    );
                }}
            />
        );
    }
);
TextFieldWithController.propTypes = {
    control: PropTypes.any,
    name: PropTypes.string,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    onKeyUp: PropTypes.func,
    normalize: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
};
TextFieldWithController.defaultProps = {
    normalize: (val) => val,
};
export default TextFieldWithController;
